import { useCallback, useState, useRef } from 'react';
import { MediaType } from '../types';
import { isHiddenFile } from '../utils';

export interface DropzoneProps {
  /** Callback to handle file uploads */
  onUpload?: (files: FileList) => void;
  children: React.ReactNode;
  disabled?: boolean;
  acceptedFileTypes?: MediaType[];
  fitToContent?: boolean;
}

async function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const result: FileSystemEntry[] = [];
  let batch: FileSystemEntry[];
  do {
    batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject)
    );
    result.push(...batch);
  } while (batch.length > 0);
  return result;
}

async function collectFilesFromEntry(entry: FileSystemEntry): Promise<File[]> {
  if (isHiddenFile(entry.name)) return [];
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject)
    );
    return [file];
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const entries = await readAllEntries(reader);
    return (await Promise.all(entries.map(collectFilesFromEntry))).flat();
  }
  return [];
}

export const Dropzone = ({
  onUpload,
  children,
  disabled = false,
  acceptedFileTypes = [],
  fitToContent = false,
}: DropzoneProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef<number>(0);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;

    // Only show overlay if dragging files from outside
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;

    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounter.current = 0;

    if (disabled || !onUpload) return;

    const entries = Array.from(e.dataTransfer.items)
      .map((item) => item.webkitGetAsEntry())
      .filter((entry): entry is FileSystemEntry => entry !== null);

    let fileArray: File[];
    if (entries.length > 0) {
      fileArray = (await Promise.all(entries.map(collectFilesFromEntry))).flat();
    } else {
      // Fallback for browsers/environments where webkitGetAsEntry is unavailable
      fileArray = Array.from(e.dataTransfer.files).filter((f) => !isHiddenFile(f.name));
    }

    if (fileArray.length > 0) {
      const dt = new DataTransfer();
      fileArray.forEach((f) => dt.items.add(f));
      onUpload(dt.files);
    }
  }, [disabled, onUpload]);

  // const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const files = event.target.files;
  //   if (files && files.length > 0 && onUpload) {
  //     onUpload(files);
  //     // Reset the input so the same file can be selected again
  //     event.target.value = '';
  //   }
  // };

  return (
    <>
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`dropzone ${isDragOver ? 'dropzone--active' : ''} ${disabled ? 'dropzone--disabled' : ''} ${fitToContent ? 'dropzone--fit-content' : ''}`}
      >
        <div className="dropzone-children-container">
          {children}
        </div>
        {isDragOver &&
          (
            <div className="dropzone-area"></div>
          )
        }
      </div>
    </>
  );
};